import React, { useEffect, useMemo, useState } from 'react'
import { MultiValue } from 'react-select'
import CreatableSelect from 'react-select/creatable'

import InputGroup from '@components/InputGroup'
import { QuestionArticleInput } from '@models/lawArticle.model'
import { disciplineApi } from '@services/DisciplineApi'

type Props = {
	title: string
	subMatterIds: number[]
	value?: QuestionArticleInput[]
	onChange: (value: QuestionArticleInput[]) => void
	followTitle?: boolean
}

type ArticleOption = { value: string; label: string }

const SUGGEST_DELAY_MS = 500

const withoutPrefix = (article: string) =>
	article.replace(/^\s*art(igo)?s?\.?\s*/i, '').trim()

const sameArticles = (a: QuestionArticleInput[], b: QuestionArticleInput[]) =>
	a.length === b.length &&
	a.every(
		(item, index) =>
			item.disciplineId === b[index].disciplineId &&
			withoutPrefix(item.article) === withoutPrefix(b[index].article)
	)

const styles = {
	wrapper: 'mt-4',
	empty: 'text-sm text-gray',
	group: 'mb-3',
	suggestion: 'mt-1 text-sm text-gray',
	useTitle: 'ml-1 text-blue underline',
}

export default function QuestionArticles({
	title,
	subMatterIds,
	value,
	onChange,
	followTitle = true,
}: Props) {
	const [suggest, { data: suggestion }] =
		disciplineApi.useSuggestQuestionArticlesMutation()
	const [edited, setEdited] = useState(!followTitle)

	const subMatterKey = subMatterIds.join(',')

	useEffect(() => {
		if (!subMatterKey) {
			return undefined
		}

		const timer = setTimeout(() => {
			suggest({
				title: title ?? '',
				submatterIds: subMatterKey.split(',').map(Number),
			})
		}, SUGGEST_DELAY_MS)

		return () => clearTimeout(timer)
	}, [title, subMatterKey, suggest])

	const groups = useMemo(() => suggestion?.data ?? [], [suggestion])

	const fromTitle = useMemo<QuestionArticleInput[]>(
		() =>
			groups.flatMap((group) =>
				group.articles.map((article) => ({
					disciplineId: group.disciplineId,
					article: article.label,
				}))
			),
		[groups]
	)

	useEffect(() => {
		if (!edited && suggestion && !sameArticles(fromTitle, value ?? [])) {
			onChange(fromTitle)
		}
	}, [edited, suggestion, fromTitle, value, onChange])

	if (subMatterIds.length === 0) {
		return (
			<InputGroup label='Artigos' id='lawArticles' className={styles.wrapper}>
				<p className={styles.empty}>
					Escolha o sub-assunto para identificar os artigos da questão.
				</p>
			</InputGroup>
		)
	}

	const current = value ?? []
	const titleDiffers = edited && suggestion && !sameArticles(fromTitle, current)

	const onGroupChange =
		(disciplineId: number) => (options: MultiValue<ArticleOption>) => {
			setEdited(true)
			onChange([
				...current.filter((item) => item.disciplineId !== disciplineId),
				...options.map((option) => ({
					disciplineId,
					article: withoutPrefix(option.value),
				})),
			])
		}

	return (
		<div className={styles.wrapper}>
			{groups.map((group) => (
				<InputGroup
					key={group.disciplineId}
					label={
						groups.length > 1 ? `Artigos - ${group.disciplineName}` : 'Artigos'
					}
					id={`lawArticles-${group.disciplineId}`}
					className={styles.group}
					hint='Lidos da tag do título. Digite um número para acrescentar (ex.: 1.015-A).'
				>
					<CreatableSelect<ArticleOption, true>
						inputId={`lawArticles-${group.disciplineId}`}
						isMulti
						value={current
							.filter((item) => item.disciplineId === group.disciplineId)
							.map((item) => ({
								value: item.article,
								label: withoutPrefix(item.article),
							}))}
						options={group.articles.map((article) => ({
							value: article.label,
							label: article.label,
						}))}
						onChange={onGroupChange(group.disciplineId)}
						isValidNewOption={(input) => /\d/.test(input)}
						formatCreateLabel={(input) =>
							`Acrescentar Art. ${withoutPrefix(input)}`
						}
						noOptionsMessage={() => 'Digite o número do artigo'}
						placeholder='Nenhum artigo'
						classNamePrefix='react-select'
					/>
				</InputGroup>
			))}
			{titleDiffers && (
				<p className={styles.suggestion}>
					O título indica{' '}
					{fromTitle.length
						? fromTitle.map((item) => `Art. ${item.article}`).join(', ')
						: 'nenhum artigo'}
					.
					<button
						type='button'
						className={styles.useTitle}
						onClick={() => {
							setEdited(false)
							onChange(fromTitle)
						}}
					>
						Usar artigos do título
					</button>
				</p>
			)}
		</div>
	)
}
